import {
  matchFareFilterCriteria,
  isTripViewModelWithinSelectedTimeBlocksFilter,
} from '@helpers/flights/flightFilters';

import { FlightSearchResultSearchParams } from '@wegoTypes/flights/flightSearchParams';
import { LegViewModel } from '@wegoTypes/flights/legViewModel';
import { TripViewModel } from '@wegoTypes/flights/tripViewModel';

import { filterParamsUniqueForDepartLeg, filterParamsUniqueForReturnLeg } from './constants';
import { providerCodesForBaggageFilter } from './constants';

export function parseFilterValuesFromSearchParam(searchParam: string | null) {
  if (!!searchParam) {
    return searchParam.split(':').map((searchParamForEachLeg) => {
      if (!!searchParamForEachLeg) {
        return searchParamForEachLeg.split(',');
      }
      return [];
    });
  }

  return [[], []];
}

export function frameFilterParamsFromFilterValues(filterValues: Array<Array<string>>) {
  return filterValues.map((filterValuesForEachLeg) => filterValuesForEachLeg.join(',')).join(':');
}

export function copyFilterParamsFromDepartLegToReturnLeg(searchParams: URLSearchParams) {
  const filterParamKeys = Object.keys(FlightSearchResultSearchParams) as Array<
    keyof typeof FlightSearchResultSearchParams
  >;

  filterParamKeys.forEach((filterParamKey) => {
    const filterParamName = FlightSearchResultSearchParams[filterParamKey];
    const filterParams = searchParams.get(filterParamName);

    if (
      filterParams &&
      !filterParamsUniqueForDepartLeg.includes(filterParamName) &&
      !filterParamsUniqueForReturnLeg.includes(filterParamName)
    ) {
      searchParams.delete(filterParamName);

      const filterValues = parseFilterValuesFromSearchParam(filterParams);
      const copiedFilterValues = [filterValues[0], filterValues[0]];

      if (copiedFilterValues[0].length || copiedFilterValues[1].length) {
        const updatedFilterParams = frameFilterParamsFromFilterValues(copiedFilterValues);

        searchParams.set(filterParamName, updatedFilterParams);
      }
    }
  });

  return searchParams;
}

export function removeReturnLegFilterParams(searchParams: URLSearchParams) {
  const filterParamKeys = Object.keys(FlightSearchResultSearchParams) as Array<
    keyof typeof FlightSearchResultSearchParams
  >;

  filterParamKeys.forEach((filterParamKey) => {
    const filterParamName = FlightSearchResultSearchParams[filterParamKey];

    if (
      filterParamName === 'depart_duration' ||
      !filterParamsUniqueForDepartLeg.includes(filterParamName)
    ) {
      const filterParams = searchParams.get(filterParamName);

      if (filterParams) {
        searchParams.delete(filterParamName);

        if (!filterParamsUniqueForReturnLeg.includes(filterParamName)) {
          const filterValues = parseFilterValuesFromSearchParam(filterParams);

          const updatedFilterValues = [filterValues[0], []];

          if (updatedFilterValues[0].length) {
            const updatedFilterParams = frameFilterParamsFromFilterValues(updatedFilterValues);

            searchParams.append(filterParamName, updatedFilterParams);
          }
        }
      }
    }
  });

  return searchParams;
}

export function removeSpecificPerLegSearchFilterParam(
  searchParams: URLSearchParams,
  isDepartLeg: boolean,
  paramsToBeDeleted: FlightSearchResultSearchParams[],
) {
  paramsToBeDeleted.forEach((paramToBeDeleted) => {
    const filterParams = searchParams.get(paramToBeDeleted);
    searchParams.delete(paramToBeDeleted);

    if (!isDepartLeg && !filterParamsUniqueForReturnLeg.includes(paramToBeDeleted)) {
      const filterValues = parseFilterValuesFromSearchParam(filterParams);
      const updatedFilterValues = [filterValues[0], []];

      if (updatedFilterValues[0].length || updatedFilterValues[1].length) {
        const updatedFilterParams = frameFilterParamsFromFilterValues(updatedFilterValues);
        searchParams.append(paramToBeDeleted, updatedFilterParams);
      }
    }
  });

  return searchParams;
}

export function filterTripViewModelsBasedOnOneLeg(
  activeLeg: number,
  tripViewModels: TripViewModel[],
  selectedStopCodes: number[],
  selectedFlightExperiences: string[],
  selectedUsdPrices: number[],
  selectedAirlineCodes: string[],
  selectedAllianceCodes: string[],
  selectedFareProviderCodes: string[],
  selectedBookingOptions: string[],
  selectedPaymentMethodIds: Array<number>,
  selectedDepartLegDepartureTimeBlocks: Array<FlightMulticityFilterState<FlightFilterTimeBlock>>,
  selectedDepartLegArrivalTimeBlocks: Array<FlightMulticityFilterState<FlightFilterTimeBlock>>,
  selectedReturnLegDepartureTimeBlocks: Array<FlightMulticityFilterState<FlightFilterTimeBlock>>,
  selectedReturnLegArrivalTimeBlocks: Array<FlightMulticityFilterState<FlightFilterTimeBlock>>,
  selectedStopoverAirportCodes: string[],
  selectedStopoverOptions: string[],
  selectedStopoverDurationRange: number[],
  selectedTripDurationRange: number[],
  selectedOriginAirportCodes: string[],
  selectedDestinationAirportCodes: string[],
  priceOptions: PriceOptions,
  isApplyBaggageFilter: boolean,
): TripViewModel[] {
  return tripViewModels.filter((tripViewModel) => {
    const legViewModel = tripViewModel.legs[activeLeg];

    const fareViewModels = tripViewModel.fares;

    const stopCodesFilter = isStopCodesFilterSatisfied(legViewModel, selectedStopCodes);

    const flightExperienceFilter = isExperienceFilterSatisfied(
      legViewModel,
      selectedFlightExperiences,
    );

    const priceFilter = isPriceFilterSatisfied(selectedUsdPrices, fareViewModels, priceOptions);

    const airlineFilter = isAirlineFilterSatisfied(legViewModel, selectedAirlineCodes);

    const allianceFilter = isAllianceFilterSatisfied(legViewModel, selectedAllianceCodes);

    const bookingSitesAndOptionsFilter = isBookingSitesAndOptionsFilterSatisfied(
      tripViewModel,
      selectedFareProviderCodes,
      selectedBookingOptions,
      isApplyBaggageFilter,
    );

    const paymentMethodFilter = isPaymentMethodFilterSatisfied(
      fareViewModels,
      selectedPaymentMethodIds,
    );

    const departLegDepartureTimeBlockFilter = isTripViewModelWithinSelectedTimeBlocksFilter(
      tripViewModel,
      selectedDepartLegDepartureTimeBlocks,
      'departLegDepartureTime',
    );

    const departLegArrivalTimeBlockFilter = isTripViewModelWithinSelectedTimeBlocksFilter(
      tripViewModel,
      selectedDepartLegArrivalTimeBlocks,
      'departLegArrivalTime',
    );

    const returnLegDepartureTimeBlockFilter = isTripViewModelWithinSelectedTimeBlocksFilter(
      tripViewModel,
      selectedReturnLegDepartureTimeBlocks,
      'returnLegDepartureTime',
    );

    const returnLegArrivalTimeBlockFilter = isTripViewModelWithinSelectedTimeBlocksFilter(
      tripViewModel,
      selectedReturnLegArrivalTimeBlocks,
      'returnLegArrivalTime',
    );

    const timesFilter =
      (activeLeg === 1 || (departLegDepartureTimeBlockFilter && departLegArrivalTimeBlockFilter)) &&
      returnLegDepartureTimeBlockFilter &&
      returnLegArrivalTimeBlockFilter;

    const stopoverAirportFilter = isStopoverAirportsFilterSatisfied(
      legViewModel,
      selectedStopoverAirportCodes,
    );
    const noChangeOfAirportAtStopoverFilter = isNoChangeOfAirportAtStopoverFilterSatisfied(
      legViewModel,
      selectedStopoverOptions,
    );

    const tripDurationFilter = isTripDurationFilterSatisfied(
      legViewModel,
      selectedTripDurationRange,
    );

    const stopoverDurationFilter = isStopoverDurationFilterSatisfied(
      legViewModel,
      selectedStopoverDurationRange,
    );

    // Origin and destination airport codes are swapped for return leg
    const originFilter = isOriginAirportsFilterSatisfied(
      legViewModel,
      activeLeg === 0 ? selectedOriginAirportCodes : selectedDestinationAirportCodes,
    );
    const destinationFilter = isDestinationAirportsFilterSatisfied(
      legViewModel,
      activeLeg === 0 ? selectedDestinationAirportCodes : selectedOriginAirportCodes,
    );

    return (
      stopCodesFilter &&
      flightExperienceFilter &&
      priceFilter &&
      airlineFilter &&
      allianceFilter &&
      bookingSitesAndOptionsFilter &&
      paymentMethodFilter &&
      timesFilter &&
      stopoverAirportFilter &&
      noChangeOfAirportAtStopoverFilter &&
      tripDurationFilter &&
      stopoverDurationFilter &&
      originFilter &&
      destinationFilter
    );
  });
}

function isStopCodesFilterSatisfied(
  legViewModel: LegViewModel,
  selectedStopCodes: Array<number>,
): boolean {
  if (selectedStopCodes.length > 0) {
    const legStopoversCount = Math.min(legViewModel.leg.stopoversCount, 2);

    return selectedStopCodes.includes(legStopoversCount);
  }

  return true;
}

function isExperienceFilterSatisfied(
  legViewModel: LegViewModel,
  selectedFlightExperiences: Array<string>,
): boolean {
  if (selectedFlightExperiences.length > 0) {
    return selectedFlightExperiences.every((selectedExperience) => {
      if (selectedExperience === 'no_overnight_flight') {
        return !legViewModel.leg.overnight;
      }

      if (selectedExperience === 'no_long_stopover') {
        return !legViewModel.leg.longStopover;
      }

      return true;
    });
  }
  return true;
}

function isPriceFilterSatisfied(
  selectedUsdPrices: number[],
  fareViewModels: FareViewModel[],
  priceOptions: PriceOptions,
) {
  if (selectedUsdPrices.length === 2) {
    const fare = fareViewModels[0]?.fare;

    if (fare) {
      const { amountUsd, totalAmountUsd } = fare.price;

      const calculatedAmount = priceOptions.showTotalPrice ? totalAmountUsd : amountUsd;

      return calculatedAmount >= selectedUsdPrices[0] && calculatedAmount <= selectedUsdPrices[1];
    }
  }

  return true;
}

function isAirlineFilterSatisfied(
  legViewModel: LegViewModel,
  selectedAirlineCodes: Array<string>,
): boolean {
  if (selectedAirlineCodes.length > 0) {
    return selectedAirlineCodes.some((selectedAirlineCode) =>
      legViewModel.leg.airlineCodes
        .map((code) => code.toLowerCase())
        .includes(selectedAirlineCode.toLowerCase()),
    );
  }
  return true;
}

function isAllianceFilterSatisfied(
  legViewModel: LegViewModel,
  selectedAllianceCodes: Array<string>,
) {
  if (selectedAllianceCodes.length > 0) {
    return selectedAllianceCodes.some((selectedAllianceCode) =>
      legViewModel.leg.allianceCodes.includes(selectedAllianceCode),
    );
  }
  return true;
}

function isBookingSitesAndOptionsFilterSatisfied(
  tripViewModel: TripViewModel,
  selectedFareProviderCodes: Array<string>,
  selectedBookingOptions: Array<string>,
  isApplyBaggageFilter: boolean,
): boolean {
  const isBookOnWego = selectedBookingOptions.includes('wego');
  const isBookWithAirlines = selectedBookingOptions.includes('airline');
  const fareViewModels = tripViewModel.fares;

  let matchProvidersAndProviderCodesFilter = true;
  if (
    selectedFareProviderCodes.length > 0 ||
    isBookOnWego ||
    isBookWithAirlines ||
    isApplyBaggageFilter
  ) {
    const fareProviderCodes = isApplyBaggageFilter
      ? selectedFareProviderCodes.concat(providerCodesForBaggageFilter)
      : selectedFareProviderCodes;
    matchProvidersAndProviderCodesFilter = fareViewModels.some(({ provider }) => {
      return matchFareFilterCriteria(provider, fareProviderCodes, isBookOnWego, isBookWithAirlines);
    });

    const matchedFares = fareViewModels.filter(({ provider }) =>
      matchFareFilterCriteria(provider, fareProviderCodes, isBookOnWego, isBookWithAirlines),
    );

    tripViewModel.filteredFares = matchedFares;
  } else {
    tripViewModel.filteredFares = fareViewModels;
  }

  return matchProvidersAndProviderCodesFilter;
}

function isPaymentMethodFilterSatisfied(
  fareViewModels: FareViewModel[],
  selectedPaymentMethodIds: Array<number>,
): boolean {
  return selectedPaymentMethodIds.length > 0
    ? selectedPaymentMethodIds.some((paymentMethod) =>
        fareViewModels.some((fareViewModel) =>
          fareViewModel.fare.paymentFees.some(
            (paymentFee) => paymentFee.paymentMethodId === paymentMethod,
          ),
        ),
      )
    : true;
}

function isStopoverAirportsFilterSatisfied(
  legViewModel: LegViewModel,
  selectedStopoverAirportCodes: Array<string>,
): boolean {
  if (selectedStopoverAirportCodes.length > 0) {
    return selectedStopoverAirportCodes.some((selectedStopoverAirportCode) =>
      legViewModel.leg.stopoverAirportCodes.includes(selectedStopoverAirportCode),
    );
  }
  return true;
}

function isNoChangeOfAirportAtStopoverFilterSatisfied(
  legViewModel: LegViewModel,
  selectedStopoverOptions: Array<string>,
): boolean {
  if (selectedStopoverOptions.length > 0) {
    let isNoChangeOfAirportAtStopover = true;
    if (legViewModel.segmentViewModels.length > 1) {
      for (let i = 0; i < legViewModel.segmentViewModels.length - 1; i++) {
        const currentSegment = legViewModel.segmentViewModels[i].segment;
        const nextSegment = legViewModel.segmentViewModels[i + 1].segment;

        if (currentSegment.arrivalAirportCode !== nextSegment.departureAirportCode) {
          isNoChangeOfAirportAtStopover = false;
          break;
        }
      }
      return isNoChangeOfAirportAtStopover;
    }
  }
  return true;
}

function isTripDurationFilterSatisfied(
  legViewModel: LegViewModel,
  selectedTripDurationRange: Array<number>,
) {
  if (selectedTripDurationRange.length > 0) {
    return (
      legViewModel.leg.durationMinutes >= selectedTripDurationRange[0] &&
      legViewModel.leg.durationMinutes <= selectedTripDurationRange[1]
    );
  }
  return true;
}

function isStopoverDurationFilterSatisfied(
  legViewModel: LegViewModel,
  selectedStopoverDurationRange: Array<number>,
): boolean {
  if (selectedStopoverDurationRange.length > 0) {
    return (
      legViewModel.leg.stopoverDurationMinutes >= selectedStopoverDurationRange[0] &&
      legViewModel.leg.stopoverDurationMinutes <= selectedStopoverDurationRange[1]
    );
  }
  return true;
}

function isOriginAirportsFilterSatisfied(
  legViewModel: LegViewModel,
  selectedOriginAirportCodes: Array<string>,
): boolean {
  if (selectedOriginAirportCodes.length > 0) {
    return selectedOriginAirportCodes.some(
      (selectedOriginAirportCode) =>
        legViewModel.leg.departureAirportCode.toLowerCase() ===
        selectedOriginAirportCode.toLowerCase(),
    );
  }
  return true;
}

function isDestinationAirportsFilterSatisfied(
  legViewModel: LegViewModel,
  selectedDestinationAirportCodes: Array<string>,
): boolean {
  if (selectedDestinationAirportCodes.length > 0) {
    return selectedDestinationAirportCodes.some(
      (selectedDestinationAirportCode) =>
        legViewModel.leg.arrivalAirportCode.toLowerCase() ===
        selectedDestinationAirportCode.toLowerCase(),
    );
  }
  return true;
}
